import React, { useState } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import { BaseFiasUrl } from 'constants/BaseUrl';
import ConfirmationDialog from 'ui/shared/ConfirmationDialog';

import { FiasObject } from '../../types/interfaces';

import LocationTabContent from './LocationTabContent';

interface AddressElementDialogProps<T extends FieldValues> {
  visible: boolean;
  onHide: () => void;
  onSubmit: (data: T) => void;
  initialData?: T;
  formMethods: UseFormReturn<T>;
  headerAdd?: string;
  headerEdit?: string;
  withoutCoordinates?: boolean;
  enableAddressExclusions?: boolean;
}

const AddressElementDialog = <
  T extends FieldValues & { cityFias?: FiasObject | null }
>(
  props: AddressElementDialogProps<T>
) => {
  const {
    visible,
    onHide,
    onSubmit,
    initialData,
    formMethods,
    headerAdd = 'Добавление адресного элемента',
    headerEdit = 'Редактирование адресного элемента',
    withoutCoordinates,
    enableAddressExclusions
  } = props;

  const {
    handleSubmit,
    reset,
    formState: { isDirty, errors },
    watch,
    trigger,
    register,
    setValue
  } = formMethods as any;

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [houses, setHouses] = useState<FiasObject[]>([]);
  const [loading, setLoading] = useState(false);

  const cityFias = watch('cityFias');

  const handleCancel = () => {
    if (isDirty) {
      setShowCancelConfirm(true);
    } else {
      reset();
      onHide();
    }
  };

  const fetchHouses = async (streetId: number, passedCityId?: number) => {
    setLoading(true);
    const response = await fetch(
      `${BaseFiasUrl}/city/${passedCityId ?? cityFias!.objectId}/street/${streetId}/house`
    );
    const data = await response.json();
    setHouses(data);
    setLoading(false);
  };

  const handleSubmitInternal = async (data: T) => {
    setIsSubmitting(true);
    try {
      const isValid = await trigger();
      if (!isValid) return;

      onSubmit(data);
      reset();
      onHide();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        visible={visible}
        onHide={handleCancel}
        header={initialData ? headerEdit : headerAdd}
        className="destination-area-dialog"
        style={{ minWidth: '743px', width: '743px', minHeight: 'fit-content' }}
        closable={!isSubmitting}
      >
        <form onSubmit={handleSubmit(handleSubmitInternal)}>
          <LocationTabContent
            isFias={watch('isFias')! || !!cityFias}
            register={register}
            setValue={setValue}
            watch={watch}
            errors={errors}
            houses={houses}
            fetchHouses={fetchHouses}
            withoutCoordinates={withoutCoordinates}
            enableAddressExclusions={enableAddressExclusions}
          />

          <div className="flex justify-content-end gap-2 mt-4">
            <Button
              type="button"
              label="Отмена"
              severity="secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
            />
            <Button
              type="submit"
              label="Применить"
              disabled={isSubmitting || loading}
              loading={isSubmitting}
            />
          </div>
        </form>
      </Dialog>

      <ConfirmationDialog
        visible={showCancelConfirm}
        onCancel={() => setShowCancelConfirm(false)}
        onContinue={() => {
          reset();
          onHide();
          setShowCancelConfirm(false);
        }}
      />
    </>
  );
};

export default AddressElementDialog;

